import React, { type ReactNode, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, Bot, CheckCircle2, Code, FileSearch, Globe2, Languages, Layers, Lock, Maximize2, MessageSquare, Receipt, Search, Sparkles, Tags, Users, Workflow, X, Zap } from "lucide-react";
import logo from "../assets/maritaca_logo.png";
import logoBranca from "../assets/maritaca_logo_branca.png";
import bgLeque from "../assets/maritaca-bg-leque.webp";
import bgCirculos from "../assets/maritaca-bg-circulos.webp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { reportApiError, reportHandledError } from "../utils/reportError";

type ModelCardData = {
  name: string;
  badge: string;
  badgeVariant: "success" | "accent" | "secondary";
  tagline: string;
  features: string[];
  context: string;
  input: string;
  output: string;
};

const FALLBACK_MODELS: ModelCardData[] = [
  {
    name: "sabia-4",
    badge: "Atual",
    badgeVariant: "success",
    tagline: "Modelo principal para tarefas que exigem raciocínio.",
    features: [
      "Contexto de 128K tokens",
      "Conhecimento até meados de 2024",
      "Ideal para análise, redação longa e RAG",
    ],
    context: "128K",
    input: "5,00",
    output: "20,00",
  },
  {
    name: "sabiazinho-4",
    badge: "Rápido",
    badgeVariant: "accent",
    tagline: "Versão econômica para alta vazão e baixa latência.",
    features: [
      "Contexto de 128K tokens",
      "5x mais barato que sabia-4",
      "Ideal para classificação e respostas curtas",
    ],
    context: "128K",
    input: "1,00",
    output: "4,00",
  },
  {
    name: "sabia-3.1",
    badge: "Legacy",
    badgeVariant: "secondary",
    tagline: "Geração anterior, mantida para compatibilidade.",
    features: [
      "Contexto de 128K tokens",
      "Conhecimento até meados de 2024",
      "Migre para sabia-4 quando possível",
    ],
    context: "128K",
    input: "5,00",
    output: "10,00",
  },
  {
    name: "sabiazinho-3",
    badge: "Legacy",
    badgeVariant: "secondary",
    tagline: "Versão rápida da geração anterior.",
    features: [
      "Contexto de 32K tokens",
      "Conhecimento até meados de 2023",
      "Migre para sabiazinho-4 quando possível",
    ],
    context: "32K",
    input: "1,00",
    output: "3,00",
  },
];

interface PublicModelApi {
  name: string;
  description?: string;
  alias?: string;
  max_tokens: number;
  knowledge_cutoff?: string;
  update_date?: string;
  input_price: number;
  output_price: number;
}

function fmtPriceBRL(p: number): string {
  return (p * 1e6).toFixed(2).replace(".", ",");
}

function fmtContext(maxTokens: number): string {
  if (maxTokens >= 1000) return `${Math.round(maxTokens / 1000)}K`;
  return String(maxTokens);
}

function classifyModel(name: string): {
  badge: string;
  badgeVariant: "success" | "accent" | "secondary";
} {
  const lower = name.toLowerCase();
  const isFast = lower.includes("zinho") || lower.includes("small");
  const m = lower.match(/-(\d+(?:\.\d+)?)/);
  const version = m ? parseFloat(m[1]) : 0;
  if (version >= 4) return isFast ? { badge: "Rápido", badgeVariant: "accent" } : { badge: "Atual", badgeVariant: "success" };
  return { badge: "Legacy", badgeVariant: "secondary" };
}

function mapApiToCard(m: PublicModelApi): ModelCardData {
  const cls = classifyModel(m.name);
  const lower = m.name.toLowerCase();
  const isFast = lower.includes("zinho") || lower.includes("small");
  const features: string[] = [];
  features.push(`Contexto de ${fmtContext(m.max_tokens)} tokens`);
  if (m.knowledge_cutoff) features.push(`Conhecimento até ${m.knowledge_cutoff}`);
  if (cls.badge === "Legacy") {
    features.push(isFast ? "Migre para sabiazinho-4 quando possível" : "Migre para sabia-4 quando possível");
  } else {
    features.push(
      isFast
        ? "Ideal para classificação e respostas curtas"
        : "Ideal para análise, redação longa e RAG"
    );
  }
  let tagline = m.description || "";
  if (!tagline) {
    if (cls.badge === "Atual") tagline = "Modelo principal para tarefas que exigem raciocínio.";
    else if (cls.badge === "Rápido") tagline = "Versão econômica para alta vazão e baixa latência.";
    else tagline = "Geração anterior, mantida para compatibilidade.";
  }
  return {
    name: m.name,
    badge: cls.badge,
    badgeVariant: cls.badgeVariant,
    tagline,
    features,
    context: fmtContext(m.max_tokens),
    input: fmtPriceBRL(m.input_price),
    output: fmtPriceBRL(m.output_price),
  };
}

const MODEL_ORDER: Record<string, number> = { success: 0, accent: 1, secondary: 2 };


type LangKey = "curl" | "python" | "javascript";

const SNIPPETS: Record<LangKey, { label: string; node: ReactNode }> = {
  curl: {
    label: "cURL",
    node: (
      <>
        <span className="text-[#f49773]">curl</span>{" "}
        <span className="text-[#bcd941]">"https://chat.maritaca.ai/api/chat/completions"</span> \
        {"\n  "}
        <span className="text-white/60">-H</span>{" "}
        <span className="text-[#bcd941]">"Content-Type: application/json"</span> \
        {"\n  "}
        <span className="text-white/60">-H</span>{" "}
        <span className="text-[#bcd941]">"Authorization: Bearer &lt;API_KEY&gt;"</span> \
        {"\n  "}
        <span className="text-white/60">-d</span> <span className="text-white/80">'</span>
        <span className="text-white/85">{"{"}</span>
        {"\n    "}
        <span className="text-[#9bd3a4]">"model"</span>: <span className="text-[#bcd941]">"sabia-4"</span>,
        {"\n    "}
        <span className="text-[#9bd3a4]">"messages"</span>: [
        {"\n      "}
        {"{ "}
        <span className="text-[#9bd3a4]">"role"</span>: <span className="text-[#bcd941]">"user"</span>,{" "}
        <span className="text-[#9bd3a4]">"content"</span>: <span className="text-[#bcd941]">"Olá, Mundo!"</span>
        {" }"}
        {"\n    "}]
        {"\n  "}
        <span className="text-white/85">{"}"}</span>
        <span className="text-white/80">'</span>
      </>
    ),
  },
  python: {
    label: "Python",
    node: (
      <>
        <span className="text-[#e2ee75]">from</span>{" "}
        <span className="text-white/85">openai</span>{" "}
        <span className="text-[#e2ee75]">import</span>{" "}
        <span className="text-white/95">OpenAI</span>
        {"\n\n"}
        <span className="text-white/95">client</span>{" "}
        <span className="text-white/60">=</span>{" "}
        <span className="text-white/95">OpenAI</span>(
        {"\n    "}
        <span className="text-[#9bd3a4]">api_key</span>=<span className="text-[#bcd941]">"&lt;API_KEY&gt;"</span>,
        {"\n    "}
        <span className="text-[#9bd3a4]">base_url</span>=<span className="text-[#bcd941]">"https://chat.maritaca.ai/api"</span>,
        {"\n"}
        )
        {"\n\n"}
        <span className="text-white/95">response</span>{" "}
        <span className="text-white/60">=</span>{" "}
        <span className="text-white/95">client</span>.<span className="text-[#f49773]">chat</span>.<span className="text-[#f49773]">completions</span>.<span className="text-[#f49773]">create</span>(
        {"\n    "}
        <span className="text-[#9bd3a4]">model</span>=<span className="text-[#bcd941]">"sabia-4"</span>,
        {"\n    "}
        <span className="text-[#9bd3a4]">messages</span>=[{"{"}<span className="text-[#bcd941]">"role"</span>: <span className="text-[#bcd941]">"user"</span>, <span className="text-[#bcd941]">"content"</span>: <span className="text-[#bcd941]">"Olá, Mundo!"</span>{"}"}],
        {"\n"}
        )
        {"\n"}
        <span className="text-[#f49773]">print</span>(<span className="text-white/95">response</span>.<span className="text-white/85">choices</span>[<span className="text-[#e2ee75]">0</span>].<span className="text-white/85">message</span>.<span className="text-white/85">content</span>)
      </>
    ),
  },
  javascript: {
    label: "JavaScript",
    node: (
      <>
        <span className="text-[#e2ee75]">import</span>{" "}
        <span className="text-white/95">OpenAI</span>{" "}
        <span className="text-[#e2ee75]">from</span>{" "}
        <span className="text-[#bcd941]">"openai"</span>;
        {"\n\n"}
        <span className="text-[#e2ee75]">const</span>{" "}
        <span className="text-white/95">client</span>{" "}
        <span className="text-white/60">=</span>{" "}
        <span className="text-[#e2ee75]">new</span>{" "}
        <span className="text-white/95">OpenAI</span>({"{"}
        {"\n  "}
        <span className="text-[#9bd3a4]">apiKey</span>: <span className="text-[#bcd941]">"&lt;API_KEY&gt;"</span>,
        {"\n  "}
        <span className="text-[#9bd3a4]">baseURL</span>: <span className="text-[#bcd941]">"https://chat.maritaca.ai/api"</span>,
        {"\n"}
        {"}"});
        {"\n\n"}
        <span className="text-[#e2ee75]">const</span>{" "}
        <span className="text-white/95">response</span>{" "}
        <span className="text-white/60">=</span>{" "}
        <span className="text-[#e2ee75]">await</span>{" "}
        <span className="text-white/95">client</span>.<span className="text-[#f49773]">chat</span>.<span className="text-[#f49773]">completions</span>.<span className="text-[#f49773]">create</span>({"{"}
        {"\n  "}
        <span className="text-[#9bd3a4]">model</span>: <span className="text-[#bcd941]">"sabia-4"</span>,
        {"\n  "}
        <span className="text-[#9bd3a4]">messages</span>: [{"{"} <span className="text-[#9bd3a4]">role</span>: <span className="text-[#bcd941]">"user"</span>, <span className="text-[#9bd3a4]">content</span>: <span className="text-[#bcd941]">"Olá, Mundo!"</span> {"}"}],
        {"\n"}
        {"}"});
        {"\n\n"}
        <span className="text-white/95">console</span>.<span className="text-[#f49773]">log</span>(<span className="text-white/95">response</span>.<span className="text-white/85">choices</span>[<span className="text-[#e2ee75]">0</span>].<span className="text-white/85">message</span>.<span className="text-white/85">content</span>);
      </>
    ),
  },
};

type UseCase = {
  id: string;
  title: string;
  icon: typeof MessageSquare;
  description: string;
  features: string[];
  recommended: string;
  file: string;
  lang: string;
  code: string;
};

const USE_CASES: UseCase[] = [
  {
    id: "support",
    title: "Atendimento ao cliente",
    icon: MessageSquare,
    description:
      "Chatbots e assistentes de suporte que entendem gírias, contexto regional e respondem no tom da sua marca.",
    features: [
      "Resposta em português natural, sem traduções truncadas",
      "Controle de tom via system prompt",
      "Latência baixa com sabiazinho-4",
    ],
    recommended: "sabiazinho-4",
    file: "atendimento.py",
    lang: "python",
    code: `response = client.chat.completions.create(
    model="sabiazinho-4",
    messages=[
        {"role": "system", "content": "Você é o assistente da TechBR. Seja cordial e direto."},
        {"role": "user", "content": "Como faço pra cancelar meu plano?"},
    ],
)
print(response.choices[0].message.content)`,
  },
  {
    id: "docs",
    title: "Análise de documentos",
    icon: FileSearch,
    description:
      "Resumo de contratos, jurisprudência, laudos médicos e relatórios. Suporte nativo a PDFs e extração estruturada.",
    features: [
      "Upload de PDF na própria chamada",
      "Resposta em JSON estruturado",
      "Contexto de 128K tokens",
    ],
    recommended: "sabia-4",
    file: "analisar_contrato.py",
    lang: "python",
    code: `with open("contrato.pdf", "rb") as f:
    file = client.files.create(file=f, purpose="user_data")

response = client.chat.completions.create(
    model="sabia-4",
    messages=[{
        "role": "user",
        "content": [
            {"type": "file", "file": {"file_id": file.id}},
            {"type": "text", "text": "Resuma as cláusulas de rescisão."},
        ],
    }],
)`,
  },
  {
    id: "rag",
    title: "RAG e busca semântica",
    icon: Search,
    description:
      "Construa sistemas que respondem usando sua base de conhecimento: políticas internas, FAQs, catálogos, documentação técnica.",
    features: [
      "Contexto longo (128K) pra documentos inteiros",
      "Citações no formato que você definir",
      "Combine com qualquer vector DB",
    ],
    recommended: "sabia-4",
    file: "rag.py",
    lang: "python",
    code: `contexto = vector_db.search(pergunta, k=5)

response = client.chat.completions.create(
    model="sabia-4",
    messages=[
        {"role": "system", "content": f"Responda usando apenas:\n{contexto}"},
        {"role": "user", "content": pergunta},
    ],
)`,
  },
  {
    id: "agents",
    title: "Agentes e function calling",
    icon: Bot,
    description:
      "Tool calling pra orquestrar APIs externas, bancos de dados e ações em sistemas legados. Roda em loop até resolver a tarefa.",
    features: [
      "Tool calling compatível com OpenAI",
      "Múltiplas tools em paralelo",
      "Respostas determinísticas com JSON schema",
    ],
    recommended: "sabia-4",
    file: "agente.py",
    lang: "python",
    code: `tools = [{
    "type": "function",
    "function": {
        "name": "buscar_pedido",
        "description": "Busca pedido pelo CPF",
        "parameters": {
            "type": "object",
            "properties": {"cpf": {"type": "string"}},
            "required": ["cpf"],
        },
    },
}]

response = client.chat.completions.create(
    model="sabia-4",
    messages=messages,
    tools=tools,
)`,
  },
  {
    id: "classify",
    title: "Classificação em escala",
    icon: Tags,
    description:
      "Triagem de tickets, análise de sentimento, moderação e categorização de milhões de itens via Batch API com 50% de desconto.",
    features: [
      "Processa milhões de itens em horas",
      "50% de desconto no preço Batch",
      "JSONL com 50k requisições por arquivo",
    ],
    recommended: "sabiazinho-4 + Batch",
    file: "lote.jsonl",
    lang: "jsonl",
    code: `{"custom_id": "1", "method": "POST", "url": "/api/chat/completions",
 "body": {"model": "sabiazinho-4", "messages": [
   {"role": "system", "content": "Classifique em: elogio, reclamação, dúvida."},
   {"role": "user", "content": "Comprei ontem e adorei!"}]}}
{"custom_id": "2", "method": "POST", "url": "/api/chat/completions",
 "body": {"model": "sabiazinho-4", "messages": [
   {"role": "system", "content": "Classifique em: elogio, reclamação, dúvida."},
   {"role": "user", "content": "Não recebi meu pedido."}]}}`,
  },
  {
    id: "content",
    title: "Geração de conteúdo",
    icon: Languages,
    description:
      "Resumos, e-mails, descrições de produto e respostas longas, todos no português que o seu cliente entende.",
    features: [
      "Streaming token a token",
      "Texto natural sem 'tradução-ês'",
      "Controle fino via temperature e top_p",
    ],
    recommended: "sabia-4",
    file: "gerar_email.py",
    lang: "python",
    code: `stream = client.chat.completions.create(
    model="sabia-4",
    messages=[{
        "role": "user",
        "content": "Escreva um e-mail confirmando reunião na sexta às 14h.",
    }],
    stream=True,
)
for chunk in stream:
    print(chunk.choices[0].delta.content or "", end="")`,
  },
];

export default function Login() {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [showResendButton, setShowResendButton] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [activeLang, setActiveLang] = useState<LangKey>("python");
  const [activeUseCase, setActiveUseCase] = useState<number>(0);

  const navigate = useNavigate();

  const urlError = new URLSearchParams(window.location.search).get("error");
  const urlErrorDescription = new URLSearchParams(window.location.search).get("error_description");

  useEffect(() => {
    if (urlError) {
      if (urlErrorDescription === "account_linked") {
        const url = new URL(window.location.href);
        url.searchParams.delete("error");
        url.searchParams.delete("error_description");
        window.history.replaceState({}, document.title, url.toString());
        loginWithRedirect({
          authorizationParams: {
            scope: "openid profile email offline_access",
            redirect_uri: window.location.origin,
          },
          appState: { returnTo: "/" },
        });
        return;
      }
      setAlertMessage(urlErrorDescription || "Erro de autenticação");
      const verifNorm = (urlErrorDescription || "").toLowerCase().replace(/-/g, "");
      if (/confirm|verif/.test(verifNorm) && /e?mails?/.test(verifNorm)) {
        setShowResendButton(true);
      }
      const url = new URL(window.location.href);
      url.searchParams.delete("error");
      url.searchParams.delete("error_description");
      window.history.replaceState({}, document.title, url.toString());
      setLoading(false);
      setIsSignupMode(false);
      setAuthOpen(true);
    }
  }, [urlError, urlErrorDescription, loginWithRedirect]);

  useEffect(() => {
    if (!isLoading && isAuthenticated) navigate("/");
  }, [isLoading, isAuthenticated, navigate]);

  const [models, setModels] = useState<ModelCardData[]>([]);
  const [modelsLoading, setModelsLoading] = useState<boolean>(true);

  useEffect(() => {
    const apiBase =
      process.env.REACT_APP_NODE_ENV !== "development"
        ? "https://chat.maritaca.ai"
        : process.env.REACT_APP_API_BASE_URL || "";
    fetch(`${apiBase}/api/public/models`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { models?: PublicModelApi[] } | null) => {
        if (data?.models?.length) {
          const cards = data.models
            .map(mapApiToCard)
            .filter((c) => c.badge !== "Legacy");
          cards.sort((a, b) => MODEL_ORDER[a.badgeVariant] - MODEL_ORDER[b.badgeVariant]);
          setModels(cards);
        } else {
          setModels(FALLBACK_MODELS.filter((c) => c.badge !== "Legacy"));
        }
      })
      .catch(() => setModels(FALLBACK_MODELS))
      .finally(() => setModelsLoading(false));
  }, []);

  const handleLogin = async () => {
    setLoading(true);
    setAlertMessage(null);
    setIsSignupMode(false);
    try {
      await loginWithRedirect({
        authorizationParams: {
          connection: "Username-Password-Authentication",
          scope: "openid profile email",
          screen_hint: "login",
          login_hint: email || undefined,
          redirect_uri: window.location.origin,
          prompt: "login",
        },
        appState: { returnTo: "/", mode: "login" },
      });
    } catch {
      setAlertMessage("Erro ao fazer login. Tente novamente.");
      setLoading(false);
    }
  };

  const handleSocialLogin = async (connection: string) => {
    setAlertMessage(null);
    try {
      await loginWithRedirect({
        authorizationParams: {
          connection,
          scope: "openid profile email",
          redirect_uri: window.location.origin,
          prompt: "login",
        },
        appState: { returnTo: "/" },
      });
    } catch {
      setAlertMessage("Erro ao fazer login social");
    }
  };

  const goToSignupDirect = () => {
    setLoading(true);
    setAlertMessage(null);
    setIsSignupMode(true);
    const domain = process.env.REACT_APP_AUTH0_DOMAIN;
    const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
    const redirectUri = encodeURIComponent(window.location.origin);
    const scope = encodeURIComponent("openid profile email");
    const signupUrl =
      `https://${domain}/authorize?` +
      `response_type=code&` +
      `client_id=${clientId}&` +
      `redirect_uri=${redirectUri}&` +
      `scope=${scope}&` +
      `screen_hint=signup&` +
      `prompt=login&` +
      `initial_screen=signUp` +
      (email ? `&login_hint=${encodeURIComponent(email)}` : "");
    window.location.href = signupUrl;
  };

  const handleResendVerification = async () => {
    if (!email) {
      setAlertMessage("Por favor, insira seu email primeiro.");
      return;
    }
    setResendLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_AUTH0_AUDIENCE_URL}/auth/resend-verification`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );
      if (response.ok) {
        setAlertMessage("Email de verificação reenviado! Verifique sua caixa de entrada.");
        setShowResendButton(false);
      } else {
        reportApiError(response, { feature: "login.resend_verification", method: "POST" });
        const errorData = await response.json();
        setAlertMessage(errorData.detail || "Erro ao reenviar email de verificação.");
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "login.resend_verification" });
      setAlertMessage("Erro ao reenviar email de verificação.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background font-sans">
      {/* Top nav */}
      <header className="sticky top-0 z-30 border-b border-primary/30 bg-primary text-primary-foreground">
        <div className="mx-auto flex h-20 w-full max-w-6xl items-center justify-between px-6">
          <div className="flex items-center gap-2">
            <img src={logoBranca} alt="Maritaca" className="h-11 w-11" />
            <span className="text-[22px] font-semibold tracking-tight text-white">
              maritaca.ai
            </span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="https://docs.maritaca.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden rounded-md px-3 py-1.5 text-[13px] font-medium text-white/70 transition-colors hover:text-white sm:block"
            >
              Documentação
            </a>
            <button
              onClick={() => setAuthOpen(true)}
              className="inline-flex h-10 items-center justify-center rounded-md bg-white px-5 text-[14px] font-medium text-primary transition-colors hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
            >
              Entrar
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative isolate overflow-hidden">
        <img
          src={bgLeque}
          alt=""
          aria-hidden="true"
          decoding="async"
          className="pointer-events-none absolute left-1/2 top-0 -z-10 w-[2200px] max-w-none -translate-x-1/2 -translate-y-[20%] opacity-20 [mask-image:radial-gradient(ellipse_at_center,#000_40%,transparent_75%)]"
        />
        <div className="mx-auto w-full max-w-6xl px-6 py-20 md:py-28">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-[12px] font-medium text-muted-foreground">
            <Sparkles className="h-3 w-3" />
            Sabiá-4 já disponível
          </span>
          <h1 className="mt-5 text-4xl font-bold tracking-tight text-foreground md:text-6xl">
            IAs treinadas<br />
            <span className="text-primary">para o Brasil</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-[15px] leading-relaxed text-muted-foreground md:text-[17px]">
            API simples e em português, com latência baixa e preço competitivo.
            Compatível com o SDK da OpenAI, troque a baseURL e comece a usar em segundos.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button size="lg" onClick={() => setAuthOpen(true)} className="gap-2">
              Comece gratuitamente
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => window.open("https://docs.maritaca.ai", "_blank", "noopener,noreferrer")}
            >
              Ver documentação
            </Button>
          </div>
          <p className="mt-4 text-[13px] text-muted-foreground">
            <strong className="text-foreground">R$ 20 de crédito</strong> na primeira recarga ou ao
            cadastrar um cartão.
          </p>
        </div>

        {/* Code preview */}
        <div className="mx-auto mt-16 max-w-3xl">
          <div className="relative overflow-hidden rounded-lg border border-border bg-[hsl(220,14%,7%)] shadow-[0_4px_8px_rgba(60,66,87,0.08),0_12px_32px_rgba(60,66,87,0.10)]">
            <div className="flex items-center justify-between border-b border-white/5 bg-white/[0.03] px-2 py-1.5">
              <div className="flex items-center gap-0.5">
                {(Object.keys(SNIPPETS) as LangKey[]).map((k) => (
                  <button
                    key={k}
                    onClick={() => setActiveLang(k)}
                    type="button"
                    className={`rounded px-2.5 py-1 text-[12px] font-medium transition-colors ${
                      activeLang === k
                        ? "bg-white/10 text-white"
                        : "text-white/40 hover:text-white/70"
                    }`}
                  >
                    {SNIPPETS[k].label}
                  </button>
                ))}
              </div>
              <span className="hidden items-center gap-1.5 pr-2 text-[11px] text-white/40 sm:flex">
                <Code className="h-3 w-3" />
                Compatível com SDK da OpenAI
              </span>
            </div>
            <pre className="overflow-x-auto whitespace-pre p-4 font-mono text-[12.5px] leading-relaxed text-white/85">
              {SNIPPETS[activeLang].node}
            </pre>
          </div>
        </div>
        </div>
      </section>
      {/* Features — bento */}
      <section className="border-y border-border bg-surface">
        <div className="mx-auto w-full max-w-6xl px-6 py-16">
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Por que Maritaca
            </p>
            <h2 className="mt-2 text-3xl font-semibold tracking-tight text-foreground">
              Pensada pra empresas brasileiras
            </h2>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
            {/* Faturamento em reais — wide */}
            <div className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 lg:col-span-2">
              <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gradient-to-br from-[#5fbf6e]/20 via-[#9bd3a4]/10 to-transparent blur-2xl" />
              <div className="relative">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#5fbf6e]/15 text-[#1a3c34]">
                    <Receipt className="h-4 w-4" />
                  </span>
                  <h3 className="text-[16px] font-semibold tracking-tight text-foreground">
                    Faturamento em reais, com NF-e
                  </h3>
                </div>
                <p className="mt-3 max-w-md text-[13.5px] leading-relaxed text-muted-foreground">
                  Pague em R$ direto pelo cartão, PIX ou boleto. NF-e brasileira pra contabilidade.
                  Sem dólar, sem IOF, sem surpresa cambial.
                </p>
                <div className="mt-5 inline-flex items-center gap-4 rounded-lg border border-border bg-background px-4 py-3">
                  <div>
                    <p className="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground">
                      Recarga
                    </p>
                    <p className="mt-0.5 font-mono text-[18px] font-semibold text-foreground">
                      R$ 1.234,56
                    </p>
                  </div>
                  <div className="h-8 w-px bg-border" />
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#5fbf6e]/15 px-2.5 py-1 text-[11px] font-semibold text-[#1a3c34]">
                    <CheckCircle2 className="h-3 w-3" />
                    NF-e emitida
                  </span>
                </div>
              </div>
            </div>

            {/* Contexto de 128K */}
            <div className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6">
              <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br from-[#e96b3a]/20 via-[#f49773]/10 to-transparent blur-2xl" />
              <div className="relative">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#e96b3a]/15 text-[#9a3514]">
                    <Maximize2 className="h-4 w-4" />
                  </span>
                  <h3 className="text-[16px] font-semibold tracking-tight text-foreground">
                    128K de contexto
                  </h3>
                </div>
                <p className="mt-3 text-[13.5px] leading-relaxed text-muted-foreground">
                  Cabe um livro inteiro, um processo jurídico ou sua base de FAQ.
                </p>
                <div className="mt-5">
                  <div className="flex items-center justify-between text-[11px] font-mono text-muted-foreground">
                    <span>0</span>
                    <span>128.000 tokens</span>
                  </div>
                  <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full bg-gradient-to-r from-[#e96b3a] to-[#f49773] transition-all duration-700 group-hover:w-full" style={{ width: "92%" }} />
                  </div>
                  <p className="mt-1.5 text-[11px] text-muted-foreground">
                    <span className="font-mono text-foreground">≈ 300 páginas</span> em uma chamada
                  </p>
                </div>
              </div>
            </div>

            {/* Privacidade LGPD */}
            <div className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6">
              <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br from-[#1a3c34]/25 via-[#2d6a5c]/10 to-transparent blur-2xl" />
              <div className="relative">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#1a3c34]/15 text-[#1a3c34]">
                    <Lock className="h-4 w-4" />
                  </span>
                  <h3 className="text-[16px] font-semibold tracking-tight text-foreground">
                    Privacidade nativa
                  </h3>
                </div>
                <p className="mt-3 text-[13.5px] leading-relaxed text-muted-foreground">
                  Dados ficam no Brasil. Sem treinamento com seus prompts.
                </p>
                <div className="mt-5 flex flex-wrap gap-1.5">
                  <span className="inline-flex items-center gap-1 rounded-full border border-[#1a3c34]/20 bg-[#1a3c34]/5 px-2 py-0.5 text-[11px] font-semibold text-[#1a3c34]">
                    <CheckCircle2 className="h-3 w-3" />
                    LGPD
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-[#1a3c34]/20 bg-[#1a3c34]/5 px-2 py-0.5 text-[11px] font-semibold text-[#1a3c34]">
                    <CheckCircle2 className="h-3 w-3" />
                    Zero-retention
                  </span>

                </div>
              </div>
            </div>

            {/* Time brasileiro — wide */}
            <div className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 lg:col-span-2">
              <div className="absolute -left-10 -bottom-10 h-40 w-40 rounded-full bg-gradient-to-tr from-[#bcd941]/25 via-[#e2ee75]/10 to-transparent blur-2xl" />
              <div className="relative">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#bcd941]/25 text-[#3d4d0a]">
                    <Users className="h-4 w-4" />
                  </span>
                  <h3 className="text-[16px] font-semibold tracking-tight text-foreground">
                    Time de IA brasileiro
                  </h3>
                </div>
                <p className="mt-3 max-w-md text-[13.5px] leading-relaxed text-muted-foreground">
                  Falar com a Maritaca é falar com o time que treinou o modelo. Engenheiros
                  brasileiros, sem ticket internacional, sem barreira de idioma.
                </p>
                <div className="mt-5 flex items-center gap-4">
                  <div className="flex -space-x-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-card bg-[#e96b3a] font-mono text-[11px] font-semibold text-white">RN</div>
                    <div className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-card bg-[#5fbf6e] font-mono text-[11px] font-semibold text-white">TS</div>
                    <div className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-card bg-[#1a3c34] font-mono text-[11px] font-semibold text-white">TL</div>
                    <div className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-card bg-muted font-mono text-[11px] font-semibold text-muted-foreground">+</div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Casos de uso — slider tabbed */}
      <section className="mx-auto w-full max-w-6xl px-6 py-16">
        <div className="max-w-2xl">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Casos de uso
          </p>
          <h2 className="mt-2 text-3xl font-semibold tracking-tight text-foreground">
            O que dá pra construir com a Maritaca
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
            Da automação de suporte a pipelines complexos de extração de dados — todos os
            exemplos abaixo funcionam com poucas linhas de código.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-[280px,1fr]">
          {/* Left: tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:gap-1 lg:overflow-visible lg:pb-0">
            {USE_CASES.map((uc, i) => {
              const Icon = uc.icon;
              const active = i === activeUseCase;
              return (
                <button
                  key={uc.id}
                  onClick={() => setActiveUseCase(i)}
                  type="button"
                  className={`group flex shrink-0 items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors lg:px-3 lg:py-3 ${
                    active
                      ? "border-foreground/30 bg-card shadow-[0_1px_2px_rgba(60,66,87,0.06)]"
                      : "border-transparent text-muted-foreground hover:border-border hover:bg-card/60 hover:text-foreground"
                  }`}
                >
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md border ${
                      active ? "border-border bg-muted text-foreground" : "border-border/60 bg-background"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span className={`text-[13.5px] font-medium ${active ? "text-foreground" : ""}`}>{uc.title}</span>
                </button>
              );
            })}
          </div>

          {/* Right: detail panel */}
          {(() => {
            const uc = USE_CASES[activeUseCase];
            const Icon = uc.icon;
            return (
              <div className="overflow-hidden rounded-xl border border-border bg-card">
                <div className="flex flex-col gap-6 p-8">
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-muted">
                      <Icon className="h-5 w-5 text-foreground" />
                    </span>
                    <h3 className="text-[22px] font-semibold tracking-tight text-foreground">
                      {uc.title}
                    </h3>
                  </div>
                  <p className="text-[15px] leading-relaxed text-muted-foreground">
                    {uc.description}
                  </p>
                  <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {uc.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-[13.5px] text-foreground/90">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between border-t border-border pt-5">
                    <div>
                      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        Modelo recomendado
                      </p>
                      <code className="mt-1 inline-block rounded border border-border bg-muted px-2 py-0.5 font-mono text-[12.5px] text-foreground">
                        {uc.recommended}
                      </code>
                    </div>
                    <a
                      href="https://docs.maritaca.ai"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[13px] font-medium text-primary hover:underline"
                    >
                      Ver na documentação
                      <ArrowRight className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* Modelos */}
      <section className="relative isolate overflow-hidden">
        <img
          src={bgCirculos}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="pointer-events-none absolute left-1/2 top-[-40%] -z-10 h-auto w-[150%] max-w-none -translate-x-1/2 opacity-15"
        />
        <div className="mx-auto w-full max-w-6xl px-6 py-16">
        <div className="max-w-2xl">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Modelos
          </p>
          <h2 className="mt-2 text-3xl font-semibold tracking-tight text-foreground">
            Família Sabiá, treinada no Brasil
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
            Modelos para diferentes cenários, do raciocínio mais avançado a respostas
            ultra-rápidas, todos otimizados para português brasileiro.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
          {modelsLoading
            ? Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="flex flex-col rounded-lg border border-border bg-card p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                    <Skeleton className="h-5 w-12 rounded-full" />
                  </div>
                  <div className="mt-4 space-y-2 border-t border-border pt-4">
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-5/6" />
                    <Skeleton className="h-3 w-2/3" />
                  </div>
                  <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
              ))
            : models.map((m) => {
                const gradient =
                  m.badge === "Atual"
                    ? "bg-gradient-to-br from-[#e96b3a] via-[#f49773] to-[#fde6d6]"
                    : m.badge === "Rápido"
                    ? "bg-gradient-to-br from-[#bcd941] via-[#e2ee75] to-[#fbfde2]"
                    : "bg-gradient-to-br from-[#5fbf6e] via-[#9bd3a4] to-[#e3f1e7]";
                return (
                  <div
                    key={m.name}
                    className={`relative flex flex-col rounded-2xl p-6 text-[#19352e] ${gradient}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-mono text-[18px] font-semibold tracking-tight">{m.name}</p>
                      <span className="rounded-full border border-[#19352e]/30 bg-white/40 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-[#19352e]">
                        {m.badge}
                      </span>
                    </div>
                    <p className="mt-3 text-[13.5px] leading-relaxed text-[#19352e]/85">{m.tagline}</p>
                    <ul className="mt-5 space-y-2 border-t border-[#19352e]/15 pt-4">
                      {m.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-[13px] text-[#19352e]/90">
                          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#19352e]/60" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto pt-5 text-[12px]">
                      <p className="text-[#19352e]/70">Por 1M de tokens</p>
                      <p className="mt-1 font-mono text-[13.5px] font-semibold text-[#19352e]">
                        R$ {m.input} input <span className="font-normal">·</span> R$ {m.output} output
                      </p>
                    </div>
                  </div>
                );
              })}
        </div>
        </div>
      </section>

      {/* Economia (Batch + Flex) */}
      <section className="mx-auto w-full max-w-6xl px-6 py-16">
        <div className="max-w-2xl">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Modos de execução
          </p>
          <h2 className="mt-2 text-3xl font-semibold tracking-tight text-foreground">
            Economize até 50% conforme o seu caso de uso
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
            Além da API padrão, oferecemos modos otimizados para reduzir custos quando latência
            não é crítica. Mesmo modelo, mesma qualidade.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-muted">
                <Layers className="h-4 w-4 text-foreground" />
              </div>
              <Badge variant="success">-50%</Badge>
            </div>
            <h3 className="mt-4 text-[17px] font-semibold tracking-tight text-foreground">Batch API</h3>
            <p className="mt-2 text-[13.5px] leading-relaxed text-muted-foreground">
              Processamento assíncrono em lote. Envie um arquivo JSONL com milhares de requisições
              e receba os resultados quando o processamento for concluído.
            </p>
            <ul className="mt-4 space-y-1.5 text-[13px] text-foreground/90">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                Ideal para classificação, extração, análise em escala
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                SLA de 24h (geralmente conclui em minutos a poucas horas)
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                Mesma interface da API padrão
              </li>
            </ul>
            <a
              href="https://docs.maritaca.ai/pt/batch-api"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-1 text-[13px] font-medium text-primary hover:underline"
            >
              Documentação Batch
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-md border border-border bg-muted">
                <Zap className="h-4 w-4 text-foreground" />
              </div>
              <Badge variant="success">-50%</Badge>
            </div>
            <h3 className="mt-4 text-[17px] font-semibold tracking-tight text-foreground">Flex</h3>
            <p className="mt-2 text-[13.5px] leading-relaxed text-muted-foreground">
              Requisições síncronas com prioridade reduzida. Mesma interface, latência maior em
              picos — perfeito para workloads tolerantes a tempo.
            </p>
            <ul className="mt-4 space-y-1.5 text-[13px] text-foreground/90">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                Endpoint síncrono com mesma API
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                Sem alteração de código além da flag
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                Ótimo para tarefas em background
              </li>
            </ul>
            <a
              href="https://docs.maritaca.ai/pt/flex-tier"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-1 text-[13px] font-medium text-primary hover:underline"
            >
              Documentação Flex
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </section>

      {/* Preços */}
      <section className="border-y border-border bg-surface">
        <div className="mx-auto w-full max-w-6xl px-6 py-16">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="max-w-xl">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Preços
              </p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight text-foreground">
                Tarifas competitivas, sem surpresas
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
                Pague apenas pelo que usar, em reais. Tarifa noturna automática (-30%) e Batch
                API com até 50% de desconto.
              </p>
            </div>
            <a
              href="https://docs.maritaca.ai/pt/precos"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[13px] font-medium text-primary hover:underline"
            >
              Ver preços completos
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>

          <div className="mt-8 overflow-hidden rounded-lg border border-border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <th className="px-5 py-3">Modelo</th>
                    <th className="px-5 py-3 text-right">Contexto</th>
                    <th className="px-5 py-3 text-right">Input / 1M</th>
                    <th className="px-5 py-3 text-right">Output / 1M</th>
                  </tr>
                </thead>
                <tbody>
                  {modelsLoading
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <tr key={i} className="border-b border-border/60 last:border-0">
                          <td className="px-5 py-3"><Skeleton className="h-4 w-28" /></td>
                          <td className="px-5 py-3"><Skeleton className="ml-auto h-4 w-12" /></td>
                          <td className="px-5 py-3"><Skeleton className="ml-auto h-4 w-16" /></td>
                          <td className="px-5 py-3"><Skeleton className="ml-auto h-4 w-16" /></td>
                        </tr>
                      ))
                    : models.map((m) => (
                        <tr key={m.name} className="border-b border-border/60 last:border-0">
                          <td className="px-5 py-3">
                            <span className="font-mono text-[13px] font-semibold text-foreground">
                              {m.name}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-right font-mono text-[13px] text-foreground">
                            {m.context}
                          </td>
                          <td className="px-5 py-3 text-right font-mono text-[13px] text-foreground">
                            R$ {m.input}
                          </td>
                          <td className="px-5 py-3 text-right font-mono text-[13px] text-foreground">
                            R$ {m.output}
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto w-full max-w-6xl px-6 py-16">
        <div className="flex flex-col items-center justify-between gap-5 rounded-xl border border-border bg-card p-8 text-center md:flex-row md:text-left">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Pronto para integrar?
            </p>
            <h3 className="mt-2 text-2xl font-semibold tracking-tight text-foreground">
              Crie sua conta e ganhe R$ 20 em créditos
            </h3>
            <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground">
              Sem cartão de crédito. Comece a testar todos os modelos imediatamente.
            </p>
          </div>
          <Button onClick={() => setAuthOpen(true)} size="lg" className="shrink-0 gap-2">
            Criar conta grátis
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-surface">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-3 px-6 py-6 sm:flex-row">
          <p className="text-[12px] text-muted-foreground">
            © {new Date().getFullYear()} Maritaca AI
          </p>
          <div className="flex items-center gap-1">
            <a
              href="https://www.maritaca.ai/termos-e-condicoes"
              target="_blank"
              rel="noreferrer"
              className="rounded px-2 py-0.5 text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Termos
            </a>
            <span className="text-muted-foreground/50">·</span>
            <a
              href="https://www.maritaca.ai/politica-de-privacidade"
              target="_blank"
              rel="noreferrer"
              className="rounded px-2 py-0.5 text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Privacidade
            </a>
            <span className="text-muted-foreground/50">·</span>
            <a
              href="https://docs.maritaca.ai"
              target="_blank"
              rel="noreferrer"
              className="rounded px-2 py-0.5 text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Documentação
            </a>
          </div>
        </div>
      </footer>

      {/* Auth dialog */}
      <Dialog
        open={authOpen}
        onOpenChange={(o) => {
          setAuthOpen(o);
          if (!o) {
            setAlertMessage(null);
            setShowResendButton(false);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Entrar na plataforma</DialogTitle>
            <DialogDescription>
              Acesse com seu email ou conta Google. Sua conta é criada automaticamente no primeiro
              acesso.
            </DialogDescription>
          </DialogHeader>

          {alertMessage && (
            <div className="relative flex flex-col gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3">
              <div className="flex items-start gap-2 pr-7">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <span className="flex-1 text-[13px] text-destructive">{alertMessage}</span>
              </div>
              {showResendButton && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                  className="w-full"
                >
                  {resendLoading ? "Reenviando..." : "Reenviar email de verificação"}
                </Button>
              )}
              <button
                type="button"
                onClick={() => {
                  setAlertMessage(null);
                  setShowResendButton(false);
                }}
                aria-label="Fechar aviso"
                className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded text-destructive transition-colors hover:bg-destructive/10"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={() => handleSocialLogin("google-oauth2")}
            className="inline-flex w-full items-center justify-center gap-2.5 rounded-md border border-border bg-background px-4 py-2.5 text-[13.5px] font-medium text-foreground transition-colors hover:bg-muted"
          >
            <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
              <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2.04a4.8 4.8 0 0 1-2.7.75 4.8 4.8 0 0 1-4.52-3.36H1.83v2.07A8 8 0 0 0 8.98 17z"/>
              <path fill="#FBBC05" d="M4.46 10.41a4.8 4.8 0 0 1-.25-1.41c0-.49.9-.97.25-1.41V5.52H1.83a8 8 0 0 0 0 7.16l2.63-2.27z"/>
              <path fill="#EA4335" d="M8.98 3.58c1.32 0 2.5.45 3.44 1.35l2.54-2.54a8 8 0 0 0-5.98-2.39 8 8 0 0 0-7.15 4.42l2.63 2.27c.63-1.89 2.39-3.11 4.52-3.11z"/>
            </svg>
            Continuar com Google
          </button>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              ou
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleLogin();
            }}
            className="flex flex-col gap-3"
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                autoFocus
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full">
              {loading && !isSignupMode ? "Entrando..." : "Entrar"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={goToSignupDirect}
              disabled={loading}
              className="w-full"
            >
              {loading && isSignupMode ? "Criando conta..." : "Não tem conta? Criar uma"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
